import './Reels.sass';

import React, {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Pager from 'react-js-pager';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';

import { Button, Header, Placeholder } from '$uikit';
import { Reel } from '$core/Reels/Reel/Reel';
import {
  canUploadReelSelector,
  reelsActions,
  reelsActiveIndexSelector,
  reelsUserFollowsSelector,
} from '$store/reels';
import { Loader, MetaTags } from '$shared/components';
import { useCurrentUserInfo, useDesktop, useTranslator } from '$hooks';
import { IcAdd28, IcAngleUp16, IcPlay28, IcSoundOff24, IcSoundOn24 } from '$assets';
import { showModal } from '$navigation/helper';
import { storiesActions, storiesSoundSelector } from '$store/stories';
import { classNames, setStorageItem } from '$utils';
import { AuthPopoverSimple } from '$core/AuthPopover/AuthPopover';
import { authIsLoggedSelector } from '$store/auth';
import { ReelsModel } from '$store/models';
import { ReelsAuth } from '$core/Reels/ReelsAuth/ReelsAuth';
import { ReelsProps } from '$core/Reels/Reels.interface';

export const Reels: FC<ReelsProps> = ({
  reels,
  loadingStatus,
  activeIndexInitial,
  loadData,
  loadReels,
}) => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const canUpload = useSelector(canUploadReelSelector);
  const activeIndex = useSelector(reelsActiveIndexSelector);
  const isDesktop = useDesktop();
  const sound = useSelector(storiesSoundSelector);
  const [isPlay, setPlay] = useState(false);
  const pagerRef = useRef<any>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isAnimating, setAnimating] = useState(false);
  const [isLoaded, setLoaded] = useState(false);
  const isLogged = useSelector(authIsLoggedSelector);
  const [isWatched, setWatched] = useState(false);
  const follows = useSelector(reelsUserFollowsSelector);
  const user = useCurrentUserInfo();

  useEffect(() => {
    if (user?.isolated) {
      location.replace('/');
    }
  }, []);

  useEffect(() => {
    setWatched(false);
    setLoaded(false);
  }, [activeIndex]);

  useEffect(() => {
    const video = document.getElementById('reels_video_player') as HTMLVideoElement;
    if (video) {
      try {
        if (isAnimating) {
          video.pause();
        } else {
          video.play()?.catch(() => {});
        }
      } catch (e) {}
    }
  }, [isAnimating]);

  useEffect(() => {
    if (videoRef.current && !isPlay) {
      videoRef.current.play()?.catch(() => {});
    }
  }, [videoRef, isPlay]);

  const handleEnded = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.play()?.catch(() => {});
    }

    if (isPlay) {
      setWatched(true);
    }
  }, [videoRef.current, isPlay]);

  const reelsPrepared = useMemo(() => {
    const result: (ReelsModel | 'auth')[] = [];
    result.push(...reels);

    if (!isLogged) {
      result.push('auth');
    }

    return result;
  }, [isLogged, reels]);

  const curVideoUrl = useMemo(() => {
    const reel = reelsPrepared[activeIndex];
    if (typeof reel === 'object' && 'videoUrl' in reel) {
      return reel.videoUrl;
    }
  }, [activeIndex, reelsPrepared]);

  const needPreload = useMemo(() => {
    return reelsPrepared
      .slice(activeIndex, activeIndex + 3)
      .filter((reel) => typeof reel === 'object' && 'videoUrl' in reel) as ReelsModel[];
  }, [reelsPrepared, activeIndex]);

  const handlePageSelected = useCallback(
    (pageNumber) => {
      setAnimating(false);

      if (loadingStatus !== 'loading' && reels.length - pageNumber < 3 && isLogged) {
        loadReels();
      }

      setStorageItem('reels_swiped', '1');
    },
    [loadingStatus, reels, isLogged],
  );

  const handleOnNavigationStart = useCallback((pageNumber) => {
    dispatch(reelsActions.setActiveIndex(pageNumber));
  }, []);

  const handleOnAnimation = useCallback(
    (e) => {
      const currentPosition = e.pagerHeight * activeIndex;
      const isStopped = Math.round(currentPosition) === Math.round(e.scrollY);
      if (isAnimating && isStopped) {
        setAnimating(false);
      } else if (!isAnimating && !isStopped) {
        setAnimating(true);
      }
    },
    [isAnimating, reels, activeIndex],
  );

  const handlePrev = useCallback(() => {
    if (pagerRef.current !== null) {
      setAnimating(true);
      pagerRef.current.previous();
    }
  }, [pagerRef, isPlay]);

  const handleNext = useCallback(() => {
    if (pagerRef.current !== null) {
      setAnimating(true);
      pagerRef.current.next();
    }
  }, [pagerRef, isPlay]);

  const handlePlay = useCallback(() => {
    if (videoRef.current) {
      try {
        if (sound) {
          videoRef.current.removeAttribute('muted');
          videoRef.current.muted = false;
        }
        videoRef.current.play()?.catch(() => {});
      } catch (e) {}
      setPlay(true);
    }
  }, [videoRef.current, setPlay, sound]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.code === 'ArrowUp') {
        handlePrev();
      } else if (e.code === 'ArrowDown') {
        handleNext();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [handlePrev, handleNext]);

  const handleSoundToggle = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const newSound = !sound;
      if (newSound) {
        videoRef.current!.muted = false;
        videoRef.current!.removeAttribute('muted');
      } else {
        videoRef.current!.muted = true;
        videoRef.current!.setAttribute('muted', 'true');
      }
      e.stopPropagation();
      dispatch(storiesActions.toggleSound());
    },
    [sound, dispatch],
  );

  function renderContent() {
    if (loadingStatus === 'loading' && reels.length === 0) {
      return <Loader isWrapped />;
    }

    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (reelsPrepared.length === 0) {
      return <Placeholder centered>{t('reels_empty')}</Placeholder>;
    }

    const upArrowDisabled = {
      Reels__nav__item_disabled: +activeIndex === 0,
    };

    const downArrowDisabled = {
      Reels__nav__item_disabled: +activeIndex === reels.length - 1,
    };

    return (
      <>
        {isDesktop && (
          <div className="Reels__nav">
            <div
              className={classNames('Reels__nav__item', upArrowDisabled)}
              onClick={handlePrev}
            >
              <IcAngleUp16 />
            </div>
            <div
              className={classNames('Reels__nav__item', downArrowDisabled)}
              onClick={handleNext}
            >
              <IcAngleUp16 />
            </div>
          </div>
        )}
        {!isPlay && (
          <div className="Reels__overlay" onClick={handlePlay}>
            <div className="Reels__overlay__play">
              <IcPlay28 />
            </div>
            <div className="Reels__overlay__tip">{t('reels_play_tip')}</div>
          </div>
        )}
        {reels.length > 0 && (
          <video
            id="reels_video_player"
            controls={false}
            className="Reel__video visible"
            ref={videoRef}
            preload="auto"
            playsInline
            muted
            autoPlay
            src={curVideoUrl}
            onCanPlay={() => setLoaded(true)}
            onEnded={handleEnded}
          />
        )}
        <Pager
          ref={pagerRef}
          orientation="vertical"
          animationStyle="scroll"
          wrapperStyle={{ width: '100%', height: '100%' }}
          onPageSelected={handlePageSelected}
          wheelScroll={false}
          onNavigationStart={handleOnNavigationStart}
          onPagerScroll={handleOnAnimation}
          initialPage={activeIndexInitial}
        >
          {reelsPrepared.map((reel, i) => {
            if (reel === 'auth') {
              return <ReelsAuth key={reel} />;
            } else {
              const isVisible =
                i === +activeIndex || i === +activeIndex + 1 || i === +activeIndex - 1;
              const isFollowed = follows.indexOf(reel.ownerId) >= 0;
              return (
                <div key={reel.id} className="Reel__wrap">
                  {isVisible && (
                    <Reel
                      isFollowed={isFollowed}
                      isVisible={isVisible}
                      isSelected={i === activeIndex}
                      reel={reel}
                      isActive={activeIndex === i && isPlay}
                      isAnimating={isAnimating}
                      index={i}
                      isLoaded={isLoaded}
                      isWatched={isWatched}
                      isPlay={isPlay}
                    />
                  )}
                </div>
              );
            }
          })}
        </Pager>
        <Helmet>
          {needPreload.map((reel) => (
            <link key={reel.id} rel="preload" as="video" href={reel.videoUrl} />
          ))}
        </Helmet>
      </>
    );
  }

  return (
    <>
      <MetaTags title="Ton.Place Clips" description="18+ TikTok clips feed" />
      <Header
        isTransparent
        showBackButton={true}
        rightIcons={[
          {
            icon: sound ? <IcSoundOn24 /> : <IcSoundOff24 />,
            onClick: handleSoundToggle,
          },
          {
            visible: canUpload,
            icon: <IcAdd28 />,
            onClick: () => showModal('reel'),
          },
        ]}
      >
        {isLogged ? '' : `Ton.Place `}
        {t('reels_title')}
      </Header>
      {renderContent()}
      <AuthPopoverSimple />
    </>
  );
};
